import { useQuery } from "@tanstack/react-query";
import firebaseService from "firebase_service/firebaseService";
import sentryIssuesClient, { SentryIssue } from "../../../api/sentryIssues";

const SENTRY_ISSUES_LAST_VISIT_KEY = "sentry_issues_last_visit";

export const NEW_SENTRY_ISSUES_QUERY_KEY = ["newSentryIssues"];

/** Issues Sentry has flagged as having come back after being resolved. */
export const isRegression = (issue: SentryIssue): boolean =>
  issue.substatus === "regressed";

/** An issue Sentry still considers newly arrived. */
export const isNewIssue = (issue: SentryIssue): boolean =>
  issue.substatus === "new";

function getStorageKey(): string {
  return `${SENTRY_ISSUES_LAST_VISIT_KEY}_${firebaseService.getUserId()}`;
}

function getLastVisit(): number {
  try {
    const value = localStorage.getItem(getStorageKey());
    return value ? Number(value) : 0;
  } catch {
    return 0;
  }
}

export function markSentryIssuesVisited(): void {
  try {
    localStorage.setItem(getStorageKey(), String(Date.now()));
  } catch {
    // Storage unavailable — the dot just stays on, which is the safe direction.
  }
}

const timestamp = (value: string | null): number =>
  value ? new Date(value).getTime() : 0;

/**
 * Whether anything worth looking at has shown up since the Sentry Issues tab
 * was last opened: an issue seen for the first time, or one that regressed
 * after being resolved.
 *
 * Sorted by last seen and bounded to one page — a brand-new issue is by
 * definition erroring now, so it ranks high. An issue that first appeared after
 * the last visit but stopped erroring long enough ago to fall off this page
 * will not raise the dot.
 */
export const useHasNewSentryIssues = () =>
  useQuery({
    queryKey: NEW_SENTRY_ISSUES_QUERY_KEY,
    queryFn: () =>
      sentryIssuesClient.list({
        query: "is:unresolved",
        statsPeriod: "14d",
        limit: 50,
      }),
    refetchInterval: 60_000,
    select: (page) => {
      const lastVisit = getLastVisit();
      if (!page.issues.length) return false;
      // Never opened the tab: anything unresolved is news.
      if (lastVisit === 0) return true;

      return page.issues.some(
        (issue) =>
          timestamp(issue.firstSeen) > lastVisit ||
          (isRegression(issue) && timestamp(issue.lastSeen) > lastVisit),
      );
    },
  });
